import { createCachedSelector, LruObjectCache } from 're-reselect';
import type { Interval } from 'luxon';

import { getLogger } from 'companion-app-components/utils/core';
import { accountsSelectors } from 'companion-app-components/flux/accounts';
import { categoriesSelectors } from 'companion-app-components/flux/categories';
import type RootState from 'companion-app-components/utils/redux-store/rootState';

import { getLoadPending as getTransactionsLoadPending, getTransactionsByAccountId } from 'data/transactions/selectors';
import { getOrCreateStringFromObject } from 'utils/objectToKeyUtils';
import { memoizeWithKeyRetrieveCache, memoizeWithKey } from 'utils/memoizeWithKey';
import { createValueCache } from 'utils/selectorsHelpers';
import {
  calculateValuesForPeriods,
  incomeFilters,
  monthlyFrequencyData,
  spendingFilters,
} from 'data/otReports/utils';
import { type OtReportProps } from './types';

const log = getLogger('data/otReports/selectors');

/**
 * Get income data grouped by frequency
 */
export const getIncomeForPeriods = createCachedSelector(
  (state: RootState) => accountsSelectors.getLoadPending(state) || categoriesSelectors.getLoadPending(state) || getTransactionsLoadPending(state),
  accountsSelectors.getReportableAccountsById,
  (_state: RootState, props: OtReportProps) => props.accountIds,
  getTransactionsByAccountId,
  categoriesSelectors.getCategoriesById,
  (_state: RootState, props: OtReportProps) => props.dateInterval as Interval,
  (_: RootState, props: OtReportProps) => props.frequency,
  (_: RootState, props: OtReportProps) => props.filterCriteria,
  (_: RootState, props: OtReportProps) => props.filterBySource,
  (isLoadPending, allAccountsByIds, accountIds, transactionsByAccountId, categoriesById, dateInterval, frequency, filterCriteria, filterBySource) => {

    // accounts, categories, and transactions must all be loaded before we attempt to calculate spending
    if (isLoadPending) {
      return null;
    }

    // turn into trace statement after fixing issues with input properties changing all the time
    log.log('calculating income for', allAccountsByIds, accountIds, dateInterval);

    // will default to a monthly frequency
    let frequencyData = monthlyFrequencyData;
    switch (frequency) {
      case 'monthly':
        frequencyData = monthlyFrequencyData;
        break;
      default:
        break;
    }
    // const parsedFilterCriteria = JSON.parse(filterCriteria); // quick fix for failing unit tests, seems unnecessary but was being passed to calculateValuesForPeriods
    const accountIdsToUse = accountIds || allAccountsByIds.keySeq();
    return calculateValuesForPeriods(
      accountIdsToUse,
      allAccountsByIds,
      categoriesById,
      transactionsByAccountId,
      dateInterval,
      frequencyData,
      incomeFilters,
      filterCriteria,
      filterBySource as string,
    );
  },
)({
  // this line sets the key to use for the cache
  keySelector: (_, props) => props.accountIds ? `${props.accountIds.toString()}${props.startDate}${props.endDate}` : 'any',
  cacheObject: new LruObjectCache({ cacheSize: 6 }),
});

/**
 * Get spending data grouped by frequency
 */
export const getSpendingForPeriods = createCachedSelector(
  (state: RootState) => accountsSelectors.getLoadPending(state) || categoriesSelectors.getLoadPending(state) || getTransactionsLoadPending(state),
  accountsSelectors.getReportableAccountsById,
  (_: RootState, props: OtReportProps) => props.accountIds,
  getTransactionsByAccountId,
  categoriesSelectors.getCategoriesById,
  (_: RootState, props: OtReportProps) => props.dateInterval as Interval,
  (_: RootState, props: OtReportProps) => props.frequency,
  createValueCache((_, props: OtReportProps) => props.filterCriteria),
  (_: RootState, props: OtReportProps) => props.filterBySource,
  (isLoadPending, allAccountsByIds, accountIds, transactionsByAccountId, categoriesById, dateInterval, frequency, filterCriteria, filterBySource) => {
    // accounts, categories, and transactions must all be loaded before we attempt to calculate spending
    if (isLoadPending) {
      return null;
    }
    const stringifiedFilterCriteria = JSON.stringify(filterCriteria);
    const accountIdsToUse = accountIds || allAccountsByIds.keySeq();
    const cacheKey = getOrCreateStringFromObject(
      'spending-periods',
      {
        allAccountsByIds,
        accountIdsToUse,
        transactionsByAccountId,
        categoriesById,
        dateInterval,
        frequency,
        stringifiedFilterCriteria,
        filterBySource,
      },
      6,
      memoizeWithKeyRetrieveCache,
    );

    // turn into trace statement after fixing issues with input properties changing all the time
    log.log('calculating spending for', allAccountsByIds, accountIds, dateInterval, frequency);
    // const parsedFilterCriteria = JSON.parse(filterCriteria);

    return memoizeWithKey(
      'spending-periods',
      () => {
        const criteria = filterCriteria as { items?: string[] };
        if (criteria && Array.isArray(criteria.items)) {
          const arrCategoryIds: string[] = [];
          categoriesById.forEach((objCategory) => {
            if (objCategory.parentId === criteria.items?.[0]) {
              arrCategoryIds.push(objCategory.id || '');
            }
          });

          if (arrCategoryIds.length > 0) {
            criteria.items.concat(arrCategoryIds);
          }
        }
        return calculateValuesForPeriods(
          accountIdsToUse,
          allAccountsByIds,
          categoriesById,
          transactionsByAccountId,
          dateInterval,
          monthlyFrequencyData,
          spendingFilters,
          filterCriteria,
          filterBySource as string,
        );
      },
      cacheKey,
      6,
    );
  },
)({
  // this line sets the key to use for the cache
  keySelector: (_state, { accountIds, dateInterval, frequency, filterCriteria }: OtReportProps) => {
    const accountIdsKey = accountIds ? `${accountIds.toString()}` : 'all';
    return `${accountIdsKey}_${dateInterval ? dateInterval.toString() : 'default'}_${frequency || ''}_${filterCriteria ? JSON.stringify(filterCriteria) : ''}`;
  },
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});
